// The compendium (issue 1044; CDM-003, CDM-004, CDM-005, CDM-008): the site's rules, searched mid-session by anyone at
// the table. Option A of the decision — a list with search; a result opens the site's own page in a second window,
// ONE named window (`tdt-compendium`) that every click reuses. The attribution is always on show: where the content
// comes from and the licence it is used under, with the site's own licence pages; each hit names its book when the
// site gave one.
//
// Issue 1045 (option C): for the DM, a creature's row — one of the families a token may carry — is draggable onto the
// board or into the turn tracker, and has a Place button (the keyboard's and the touch screen's way) that opens the
// same "Place <entry>" dialog at the board's centre of view. A player's rows are neither.
//
// Issue 1055: for the DM, an item's or a spell's row has "Give to…": the characters whose tokens stand on the board the
// DM is looking at (name and player), and a click gives the entry to that character's sheet on the site, saying in
// words what went to whom, or why not.
//
// Issue 1585 (slice 1): before anything is typed the panel opens on its default view, groups in a fixed order
// (`HOME_ORDER`): the person's site favourites, and the entries on the sheets at the table (every placed character's
// items for the DM, a player's own character's gear and abilities), read once on opening and again when the box is
// cleared. Typing replaces it with the results; clearing brings it back. Both list rows the same way (`EntryRow`).
//
// Slice 2: the groups that come from the table itself (`compendiumHome.ts`), made on each render from what THIS viewer's
// store and log hold — the DM's whole state, a player's projection (the person viewed as, under View as) — so a group
// shows a player nothing they were not sent. The DM's: what the scene's tokens carry, the casts in the log, the
// conditions in play, what the Loot & shops tool last showed on this scene, and what they last opened here. A player's:
// the casts in their log, the conditions on their own tokens, and the creatures whose names the DM told them.
//
// Slice 3: every entry's row raises the site's hover card for it (`EntryCard`; the row's hover intent, keyboard focus or
// a press-and-hold), one at a time, closed when the list scrolls. Its buttons are the row's own actions — Open (and
// Recently opened), the DM's Place or Give to… — and ★ Star / Unstar, whose state is the Favourites the home read gave;
// a toggle asks this origin to change the caller's site favourite and then reads the groups again, quietly. A player's
// "Revealed by the DM" rows are entries now: a told name brings the entry's address.
//
// qa-runs 407: for the DM (the `notes` power, as the DM screen is), every entry's row and card has Pin to DM screen;
// once pinned it shows ★ Pinned, with Unpin. The pins are the DM's own site DM screen for the table's game system, read
// on opening, and written on the site through this origin as Give is. A player has none of it.
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { PanelMemory } from '../keptBoard';
import { conditionEntries, type ConditionEntry } from '../../conditions';
import { conditionsInPlay, mightFindHere, onThisScene, onYou, recentlyCast, recentlyOpened, rememberOpened, revealedByDm, type TableRow } from '../../compendiumHome';
import { compendiumFamilies, compendiumHome, givableFamilies, giveEntry, searchCompendium, setFavourite, type CompendiumFamily, type CompendiumHit, type GiveAnswer, type GiveKind, type HomeGroup } from '../../compendium';
import { listTableCharacters, type TableCharacter } from '../../characters';
import { siteUrl } from '../../session';
import { useShell } from '../context';
import { useOptionalTableState } from '../../table/TableState';
import { holdsAs } from '../../table/mayAct';
import { placeableSegments, setEntryDrag } from '../../place';
import { PlaceDialog } from './PlaceDialog';
import { CompendiumGroup, EntryRow, TextRow } from './CompendiumParts';
import { DM_SCREEN_CHANGED, pinEntry, readDmScreen, unpinTile } from '../../dmScreen';
import { EntryCard } from './EntryCard';

const DEBOUNCE_MS = 300;
export const COMPENDIUM_WINDOW = 'tdt-compendium';

type Shown = { kind: 'idle' } | { kind: 'loading' } | { kind: 'error' } | { kind: 'ok'; q: string; hits: CompendiumHit[] };
/** The default view's groups, in this order, the DM's and a player's; a group with no row is not shown (On you says so). */
export const HOME_ORDER = ['favourites', 'scene', 'cast', 'conditions', 'gear', 'loot', 'opened'] as const;
export const PLAYER_HOME_ORDER = ['favourites', 'gear', 'cast', 'onyou', 'revealed'] as const;
type Home = { kind: 'loading' } | { kind: 'error' } | { kind: 'ok'; groups: HomeGroup[] };
/** An entry the row's actions act on. */
type Entry = Pick<CompendiumHit, 'segment' | 'slug' | 'name'>;
const rankIn = (order: readonly string[], key: string) => { const i = order.indexOf(key); return i < 0 ? order.length : i; };
/** A group of the default view: the site's (`HomeGroup`), or one made from the table. */
type Shelf = { key: string; title: string; site: HomeGroup } | { key: string; title: string; rows: TableRow[]; emptyText?: string };
const same = (a: Entry | null, b: Entry) => !!a && a.segment === b.segment && a.slug === b.slug;

export function CompendiumPanel() {
  const { campaignId, table } = useShell();
  const tableState = useOptionalTableState();
  // Placing and giving are the `tokens` power's routes (issue 1160): the DM, or a co-DM who holds it, whichever board.
  const isDm = (tableState ? holdsAs(tableState.role, tableState.powers ?? null, 'tokens') : table.me.role === 'dm') && !tableState?.viewingAs;
  const [placeable, setPlaceable] = useState<ReadonlySet<string>>(new Set());
  const [placing, setPlacing] = useState<Entry | null>(null);
  const [givable, setGivable] = useState<ReadonlyMap<string, GiveKind>>(new Map());
  const [giving, setGiving] = useState<Entry | null>(null);
  const [home, setHome] = useState<Home>({ kind: 'loading' });
  const homeAsked = useRef(0);
  // The search kept across a layout switch (qa-runs 1199): a switch rebuilds every panel, and the search typed here comes
  // back with this one, searched again.
  const memory = useContext(PanelMemory);
  const keptSearch = memory?.get(`compendium:${campaignId}`) as { q: string; family: string | null } | undefined;
  const [q, setQ] = useState(() => keptSearch?.q ?? '');
  const [family, setFamily] = useState<string | null>(() => keptSearch?.family ?? null);
  useEffect(() => { memory?.set(`compendium:${campaignId}`, { q, family }); }, [memory, campaignId, q, family]);
  const [families, setFamilies] = useState<CompendiumFamily[]>([]);
  const [shown, setShown] = useState<Shown>({ kind: 'idle' });
  const asked = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [conditionList, setConditionList] = useState<ConditionEntry[]>([]);
  // Counts the DM's opens, so Recently opened (read from this browser's storage) is read again.
  const [opens, setOpens] = useState(0);
  // The one row whose hover card is up (by the row's key), and the entries whose ★ is being changed now.
  const [cardKey, setCardKey] = useState<string | null>(null);
  const [starring, setStarring] = useState<ReadonlySet<string>>(new Set());
  const hasTable = !!tableState;
  // The DM's pins (qa-runs 407): the DM screen's tiles by address, and the addresses being pinned or unpinned now.
  const mayPin = (tableState ? holdsAs(tableState.role, tableState.powers ?? null, 'notes') : table.me.role === 'dm') && !tableState?.viewingAs;
  const [pins, setPins] = useState<ReadonlyMap<string, string>>(new Map());
  const [pinning, setPinning] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!mayPin) { setPins(new Map()); return undefined; }
    let live = true;
    const read = () => {
      Promise.resolve().then(() => readDmScreen(campaignId)).then((got) => {
        if (live && got.kind === 'ok') setPins(new Map(got.tiles.map((t) => [`${t.segment}/${t.slug}`, t.id])));
      }).catch(() => undefined);
    };
    read();
    window.addEventListener(DM_SCREEN_CHANGED, read);
    return () => { live = false; window.removeEventListener(DM_SCREEN_CHANGED, read); };
  }, [campaignId, mayPin]);

  useEffect(() => {
    if (!hasTable) return undefined;
    let live = true;
    Promise.resolve().then(() => conditionEntries(campaignId)).then((list) => { if (live) setConditionList(list ?? []); }).catch(() => undefined);
    return () => { live = false; };
  }, [campaignId, hasTable]);

  useEffect(() => {
    let live = true;
    compendiumFamilies(campaignId).then((list) => { if (live) setFamilies(list ?? []); }).catch(() => undefined);
    return () => { live = false; };
  }, [campaignId]);
  useEffect(() => {
    if (!isDm) { setPlaceable(new Set()); return undefined; }
    let live = true;
    placeableSegments(campaignId).then((list) => { if (live) setPlaceable(new Set(list)); }).catch(() => undefined);
    return () => { live = false; };
  }, [campaignId, isDm]);
  useEffect(() => {
    if (!isDm) { setGivable(new Map()); return undefined; }
    let live = true;
    givableFamilies(campaignId).then((m) => { if (live) setGivable(m); }).catch(() => undefined);
    return () => { live = false; };
  }, [campaignId, isDm]);
  useEffect(() => () => clearTimeout(timer.current), []);
  /** The default view's groups, read now; an answer to an older read is dropped. A failure shows none and says so. */
  // Quietly (after a ★): the groups stay on show until the new answer, and a failure keeps them.
  const loadHome = useCallback((quiet = false) => {
    const n = ++homeAsked.current;
    if (!quiet) setHome({ kind: 'loading' });
    Promise.resolve().then(() => compendiumHome(campaignId)).then((groups) => {
      if (n === homeAsked.current && (groups || !quiet)) setHome(groups ? { kind: 'ok', groups } : { kind: 'error' });
    }).catch(() => { if (n === homeAsked.current && !quiet) setHome({ kind: 'error' }); });
  }, [campaignId]);
  useEffect(() => { loadHome(); }, [loadHome]);
  // A kept search (qa-runs 1199) is run again as the panel comes back.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (q.trim()) void run(q, family); }, []);

  /** Search now; an answer to an older question is dropped. */
  const run = async (text: string, fam: string | null) => {
    clearTimeout(timer.current);
    const n = ++asked.current;
    if (!text.trim()) { setShown({ kind: 'idle' }); return; }
    setShown({ kind: 'loading' });
    const got = await searchCompendium(campaignId, text, fam).catch(() => ({ kind: 'error' as const }));
    if (n !== asked.current) return;
    setShown(got.kind === 'ok' ? { kind: 'ok', q: text.trim(), hits: got.hits } : { kind: 'error' });
  };
  const typed = (text: string) => {
    setQ(text);
    clearTimeout(timer.current);
    // Cleared: the default view at once, read afresh; nothing is searched.
    if (!text.trim()) { if (shown.kind !== 'idle' || q.trim()) { void run('', family); loadHome(); } return; }
    timer.current = setTimeout(() => { void run(text, family); }, DEBOUNCE_MS);
  };
  const chosen = (fam: string | null) => { setFamily(fam); void run(q, fam); };
  /** Open the site's page; the DM's open is remembered for Recently opened. */
  const open = (url: string, e?: Entry & { family?: string }) => {
    window.open(`${siteUrl()}${url}`, COMPENDIUM_WINDOW);
    if (isDm && e) { rememberOpened(table.me.id, campaignId, { name: e.name, segment: e.segment, slug: e.slug, ...(e.family ? { family: e.family } : {}) }); setOpens((n) => n + 1); }
  };
  const addressOf = (e: Pick<Entry, 'segment' | 'slug'>) => `${e.segment}/${e.slug}`;
  const starred = new Set(home.kind === 'ok' ? (home.groups.find((g) => g.key === 'favourites')?.rows ?? []).map(addressOf) : []);
  const say = (text: string) => { if (tableState) tableState.setNotice(text); };
  /** Star or unstar: the caller's own site favourite, then the groups read again so Favourites shows it. */
  const star = async (e: Entry) => {
    const at = addressOf(e);
    const now = !starred.has(at);
    setStarring((s) => new Set(s).add(at));
    const r = await setFavourite(campaignId, e.segment, e.slug, now).catch(() => ({ kind: 'error' as const }));
    setStarring((s) => { const next = new Set(s); next.delete(at); return next; });
    if (r.kind === 'ok') { say(now ? `${e.name} is in your favourites.` : `${e.name} is no longer in your favourites.`); loadHome(true); return; }
    say(r.kind === 'refused' ? r.reason : `${e.name} could not be ${now ? 'starred' : 'unstarred'} just now. Try again in a moment.`);
  };
  /** Pin or unpin on the DM's site DM screen; the DM screen at the table is told to read again. */
  const pin = async (e: Entry) => {
    const at = addressOf(e);
    const tileId = pins.get(at);
    setPinning((s) => new Set(s).add(at));
    const r: { kind: 'ok'; id: string | null } | { kind: 'refused'; reason: string } | { kind: 'error' } = tileId
      ? await unpinTile(campaignId, tileId).then((x) => (x.kind === 'ok' ? { kind: 'ok' as const, id: null } : x)).catch(() => ({ kind: 'error' as const }))
      : await pinEntry(campaignId, e.segment, e.slug).then((x) => (x.kind === 'ok' ? { kind: 'ok' as const, id: x.tile.id } : x)).catch(() => ({ kind: 'error' as const }));
    setPinning((s) => { const next = new Set(s); next.delete(at); return next; });
    if (r.kind === 'ok') {
      setPins((m) => { const next = new Map(m); if (tileId) next.delete(at); else if (r.id) next.set(at, r.id); return next; });
      say(tileId ? `${e.name} is no longer on your DM screen.` : `${e.name} is pinned to your DM screen.`);
      window.dispatchEvent(new Event(DM_SCREEN_CHANGED));
      return;
    }
    say(r.kind === 'refused' ? r.reason : `${e.name} could not be ${tileId ? 'unpinned' : 'pinned'} just now. Try again in a moment.`);
  };
  const pageOf = (e: Entry) => `/${e.segment}?slug=${encodeURIComponent(e.slug)}&system=${encodeURIComponent(table.campaignSystem)}`;

  /** One entry's row, in a group or the results: the DM's Place, drag and Give to… where they apply. */
  const row = (testid: string, key: string, e: Entry & { family: string; book?: string | null | undefined; detail?: string | undefined; hidden?: boolean | undefined }, url: string) => {
    const mayPlace = isDm && !!tableState && placeable.has(e.segment);
    const giveKind = isDm && tableState ? givable.get(e.segment) : undefined;
    const entry = { segment: e.segment, slug: e.slug, name: e.name };
    const preview = {
      open: cardKey === key,
      onOpenChange: (o: boolean) => setCardKey((k) => (o ? key : k === key ? null : k)),
      card: (anchor: HTMLElement | null) => (
        <EntryCard campaignId={campaignId} segment={e.segment} slug={e.slug} name={e.name} family={e.family} anchor={anchor}
          starred={starred.has(addressOf(e))} starring={starring.has(addressOf(e))}
          pinned={pins.has(addressOf(e))} pinning={pinning.has(addressOf(e))} onPin={mayPin ? () => { void pin(entry); } : undefined}
          onOpen={() => { setCardKey(null); open(url, e); }}
          onStar={() => { void star(entry); }}
          onPlace={mayPlace ? () => { setCardKey(null); setPlacing(entry); } : undefined}
          onGive={giveKind !== undefined ? () => { setCardKey(null); setGiving(entry); } : undefined} />
      ),
    };
    return (
      <EntryRow key={key} testid={testid} name={e.name} family={e.family} book={e.book} detail={e.detail} hidden={e.hidden}
        title={mayPlace ? "Opens the site's page in the compendium window; drag it onto the board or the turn tracker to place it" : "Opens the site's page in the compendium window"}
        onOpen={() => open(url, e)} preview={preview}
        onDragStart={mayPlace ? (ev) => { setCardKey(null); setEntryDrag(ev.dataTransfer, { segment: e.segment, slug: e.slug, name: e.name }); } : undefined}
        actions={<>
          {mayPlace && (
            <button type="button" className="shrink-0 rounded border border-stone-600 px-2 py-0.5 text-xs hover:bg-stone-800" aria-label={`Place ${e.name} on the board`} onClick={() => { setCardKey(null); setPlacing(e); }}>Place</button>
          )}
          {mayPin && (pins.has(addressOf(e))
            ? <>
              <span className="shrink-0 text-xs text-vtt-gold" data-testid={`compendium-pinned-${e.segment}-${e.slug}`}>★ Pinned</span>
              <button type="button" className="shrink-0 rounded border border-stone-600 px-2 py-0.5 text-xs hover:bg-stone-800 disabled:opacity-50" disabled={pinning.has(addressOf(e))}
                aria-label={`Unpin ${e.name} from your DM screen`} data-testid={`compendium-unpin-${e.segment}-${e.slug}`} onClick={() => { setCardKey(null); void pin(entry); }}>Unpin</button>
            </>
            : <button type="button" className="shrink-0 rounded border border-stone-600 px-2 py-0.5 text-xs hover:bg-stone-800 disabled:opacity-50" disabled={pinning.has(addressOf(e))}
              aria-label={`Pin ${e.name} to your DM screen`} title="Pin to DM screen" data-testid={`compendium-pin-${e.segment}-${e.slug}`} onClick={() => { setCardKey(null); void pin(entry); }}>Pin</button>)}
          {giveKind !== undefined && (
            <button type="button" className="shrink-0 rounded border border-stone-600 px-2 py-0.5 text-xs hover:bg-stone-800"
              aria-label={`Give ${e.name} to a character`} aria-expanded={same(giving, e)}
              data-testid={`compendium-give-${e.segment}-${e.slug}`}
              onClick={() => { setCardKey(null); setGiving(same(giving, e) ? null : { segment: e.segment, slug: e.slug, name: e.name }); }}>Give to…</button>
          )}
        </>} />
    );
  };
  // The groups made from the table, from what this viewer holds (the person viewed as, under View as).
  const familyLabels = new Map(families.map((f) => [f.segment, f.label]));
  const shelves: Shelf[] = home.kind === 'ok' ? home.groups.map((g) => ({ key: g.key, title: g.title, site: g })) : [];
  if (tableState) {
    const objects = tableState.store.state.objects;
    const cast = recentlyCast(tableState.log, Date.now() - tableState.clockSkew, familyLabels);
    if (isDm) {
      const sceneId = tableState.scene?.id ?? null;
      void opens;
      shelves.push(
        { key: 'scene', title: 'On this scene', rows: onThisScene(objects, familyLabels) },
        { key: 'cast', title: 'Recently cast', rows: cast },
        { key: 'conditions', title: 'Conditions in play', rows: conditionsInPlay(objects, conditionList, familyLabels) },
        { key: 'loot', title: 'Might find here', rows: mightFindHere(table.me.id, campaignId, sceneId, familyLabels) },
        { key: 'opened', title: 'Recently opened', rows: recentlyOpened(table.me.id, campaignId, familyLabels) },
      );
    } else {
      shelves.push(
        { key: 'cast', title: 'Recently cast', rows: cast },
        { key: 'onyou', title: 'On you', rows: onYou(objects, tableState.meId, conditionList, familyLabels), emptyText: 'None right now' },
        { key: 'revealed', title: 'Revealed by the DM', rows: revealedByDm(objects, tableState.meId, familyLabels) },
      );
    }
  }
  const homeOrder: readonly string[] = isDm ? HOME_ORDER : PLAYER_HOME_ORDER;
  const groups = shelves.filter((g) => homeOrder.includes(g.key)).sort((a, b) => rankIn(homeOrder, a.key) - rankIn(homeOrder, b.key));
  /** One table row: an entry's, with its actions; or plain text when there is no entry to open. */
  const tableRow = (g: string, r: TableRow, i: number) => (r.segment && r.slug
    ? row(`compendium-row-${r.segment}-${r.slug}`, `${g} ${r.segment}/${r.slug} ${i}`, { segment: r.segment, slug: r.slug, name: r.name, family: r.family ?? '', detail: r.detail, hidden: r.hidden }, pageOf({ segment: r.segment, slug: r.slug, name: r.name }))
    : <TextRow key={`${g} text ${i}`} testid={`compendium-text-${g}-${i}`} name={r.name} detail={r.detail} />);

  const site = siteUrl();
  return (
    <div className="flex h-full flex-col p-2 text-sm" data-testid="compendium">
      <div className="flex gap-2">
        <input type="search" aria-label="Search the compendium" placeholder="Search by name…" maxLength={100}
          className="min-w-0 flex-1 rounded bg-stone-800 px-2 py-1" value={q}
          onChange={(e) => typed(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void run(q, family); } }} />
        <select aria-label="Family" className="rounded bg-stone-800 px-1 py-1" value={family ?? ''} onChange={(e) => chosen(e.target.value || null)}>
          <option value="">All</option>
          {families.map((f) => <option key={f.segment} value={f.segment}>{f.label}</option>)}
        </select>
      </div>
      <div className="mt-2 min-h-0 flex-1 overflow-auto" onScroll={() => setCardKey(null)}>
        {shown.kind === 'idle' && (
          <div data-testid="compendium-home">
            <p data-testid="compendium-state" className="mb-2 text-xs text-stone-400">Type a name to search the rules on The Dragon's Table.</p>
            {home.kind === 'loading' && <p data-testid="compendium-home-state" role="status" className="text-xs text-stone-400">Reading your favourites and gear…</p>}
            {home.kind === 'error' && <p data-testid="compendium-home-state" className="text-xs text-stone-500">Your favourites and gear can't be read just now.</p>}
            {groups.map((g) => ('site' in g ? (
              <CompendiumGroup key={g.key} groupKey={g.key} title={g.key === 'favourites' ? `★ ${g.title}` : g.title} userId={table.me.id} count={g.site.rows.length}
                row={(i) => { const r = g.site.rows[i]!; return row(`compendium-row-${r.segment}-${r.slug}`, `${g.key} ${r.segment}/${r.slug} ${i}`, r, pageOf(r)); }} />
            ) : (
              <CompendiumGroup key={g.key} groupKey={g.key} title={g.title} userId={table.me.id} count={g.rows.length}
                {...(g.emptyText ? { emptyText: g.emptyText } : {})} row={(i) => tableRow(g.key, g.rows[i]!, i)} />
            )))}
          </div>
        )}
        {shown.kind === 'loading' && <p data-testid="compendium-state" role="status" className="text-stone-400">Searching…</p>}
        {shown.kind === 'error' && <p data-testid="compendium-state" role="alert" className="text-amber-300">The site can't be searched just now. Try again in a moment.</p>}
        {shown.kind === 'ok' && shown.hits.length === 0 && <p data-testid="compendium-state" className="text-stone-400">Nothing on the site matches “{shown.q}”.</p>}
        {shown.kind === 'ok' && shown.hits.length > 0 && (
          <ul className="divide-y divide-stone-800">
            {shown.hits.map((h) => row(`compendium-hit-${h.segment}-${h.slug}`, `${h.segment}/${h.slug}`, h, h.url))}
          </ul>
        )}
      </div>
      {giving && givable.get(giving.segment) && (
        <GiveList hit={giving} kind={givable.get(giving.segment)!} sceneId={tableState?.scene?.id ?? null} onClose={() => setGiving(null)} />
      )}
      {placing && <PlaceDialog entry={{ segment: placing.segment, slug: placing.slug, name: placing.name }} at={null} tracker={false} onClose={() => setPlacing(null)} />}
      <p data-testid="compendium-attribution" className="mt-2 border-t border-stone-800 pt-2 text-xs text-stone-500">
        Content from The Dragon's Table. Open Game Content used under the Open Game License v1.0a.{' '}
        <a className="underline" href={`${site}/ogl`} target={COMPENDIUM_WINDOW}>Open Game License</a>
        {' · '}
        <a className="underline" href={`${site}/licenses`} target={COMPENDIUM_WINDOW}>Licenses</a>
      </p>
    </div>
  );
}

/**
 * The DM's "Give to…" for one hit (issue 1055): the characters whose tokens are on the board the DM is looking at, each
 * a button; a click gives the entry to that character's sheet and says what the server said, in words. The Loot & shops
 * tool (issue 1156) gives a hoard's or a shop's item through this same list, and is told whom it went to (`onGiven`).
 */
export function GiveList({ hit, kind, sceneId, onClose, onGiven, send }: {
  hit: Pick<CompendiumHit, 'segment' | 'slug' | 'name'>; kind: GiveKind; sceneId: string | null; onClose: () => void; onGiven?: (to: string) => void;
  /** How the give is sent, when not the compendium's own (a held hoard's item, which then leaves the hoard). */
  send?: (characterId: string) => Promise<GiveAnswer>;
}) {
  const { campaignId } = useShell();
  const [list, setList] = useState<TableCharacter[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    let live = true;
    setList(null); setFailed(false); setSaid(null);
    listTableCharacters(campaignId, sceneId).then((got) => {
      if (!live) return;
      setFailed(got === null);
      setList((got ?? []).filter((c) => c.linked.length > 0));
    }).catch(() => { if (live) { setFailed(true); setList([]); } });
    return () => { live = false; };
  }, [campaignId, sceneId, hit]);
  const give = async (c: TableCharacter) => {
    setBusy(true);
    const r = await (send ? send(c.id) : giveEntry(campaignId, { characterId: c.id, kind, segment: hit.segment, slug: hit.slug, sceneId })).catch((): GiveAnswer => ({ kind: 'error' }));
    setBusy(false);
    if (r.kind === 'ok') onGiven?.(c.name);
    setSaid(r.kind === 'ok' ? { ok: true, text: r.said } : { ok: false, text: r.kind === 'refused' ? r.reason : `${hit.name} could not be given right now. Try again in a moment.` });
  };
  return (
    <section className="mt-2 rounded border border-stone-700 p-2" data-testid="compendium-give" aria-label={`Give ${hit.name} to a character`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-stone-300">Give <span className="text-stone-100">{hit.name}</span> {kind === 'item' ? 'to the Gear of' : 'to the spells of'}…</p>
        <button type="button" className="rounded px-2 py-0.5 text-xs hover:bg-stone-800" aria-label="Close the give list" data-testid="compendium-give-close" onClick={onClose}>Close</button>
      </div>
      {list === null && <p className="text-stone-400" role="status">Reading the characters on the board…</p>}
      {failed && <p className="text-amber-300" role="alert">The characters at this table could not be read right now.</p>}
      {list !== null && !failed && list.length === 0 && (
        <p className="text-stone-400" data-testid="compendium-give-none">No token on this board stands for a site character. Link a token to a character first.</p>
      )}
      {list !== null && list.length > 0 && (
        <ul className="mt-1 space-y-1">
          {list.map((c) => (
            <li key={c.id}>
              <button type="button" disabled={busy} className="w-full rounded border border-stone-700 px-2 py-1 text-left hover:bg-stone-800 disabled:opacity-50"
                aria-label={`Give ${hit.name} to ${c.name}`} data-testid={`compendium-give-to-${c.id}`} onClick={() => void give(c)}>
                <span className="text-stone-100">{c.name}</span>
                {c.owner.handle && <span className="text-stone-500">{' · '}@{c.owner.handle}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {said && <p data-testid="compendium-give-result" role="status" className={`mt-1 ${said.ok ? 'text-emerald-300' : 'text-amber-300'}`}>{said.text}</p>}
    </section>
  );
}
